
import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withScope } from '@bingads-webui-react/scope-service';
import {
  StepContainer
} from '@bingads-webui-campaign-react/goal-creation-wizard';
import {
  errorCodeMapping as gtmErrorCodeMapping,
  titleArray,
  defaultConfig,
} from '@bingads-webui-campaign-react/conversion-wizard-components';
import { ThirdPartyTagView, useDependencies } from '@bingads-webui-campaign-react/edit-website-pages';
import { UetSetupWizardContext } from '../../uet-setup-wizard-context';
import { WIZARD_ACTIONS } from '../../uet-setup-wizard-reducer';

export const GtmBaseTagSetupStep = withScope((props) => {
  const {
    selectedPlatform,
    serverContext,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(UetSetupWizardContext);
  const { logActivity } = wizardContext;
  const [footerConfigId, setFooterConfigId] = useState(1);
  const { sessionId: sessionIdInContext, IsGTMCloningChecked } = wizardContext.GTMContext;
  const { Id: tagId } = wizardContext.tagContext;
  const tagContext = {
    data: {
      id: tagId,
      type: 'UET',
    },
    isLoading: false,
    error: null,
    getTag: _.noop,
    createTag: _.noop,
  };
  const [token, repository, isAllDependenciesLoaded] = useDependencies({ serverContext });

  const { Id: accountId, CurrencyCode, TimeZone: timeZoneId } = serverContext.CurrentAccount || {};
  const { Id: userId, Name: userName } = serverContext.CurrentUser;
  const [taskItemBasics, setTaskItemBasics] = useState({
    ...defaultConfig(IsGTMCloningChecked),
    TimeZoneId: timeZoneId,
    AccountId: accountId,
    UserId: userId,
    UserName: userName,
  });
  const [gtmImportContext, setGtmImportContext] = useState({ '@odata.type': '#Model.GTMImportContext', CurrencyCode });

  const [isPollTaskStarted, setIsPollTaskStarted] = useState(false);

  useEffect(() => {
    logActivity.trace(`[GoalCreationWizard] platform: ${selectedPlatform}`);
  }, [logActivity, selectedPlatform]);

  useEffect(() => {
    if (footerConfigId) {
      logActivity.trace(`[${selectedPlatform}] render page ${footerConfigId}`);
    }
  }, [footerConfigId, logActivity, selectedPlatform]);

  const updateGTMImportContext = useCallback((updatedContext) => {
    setGtmImportContext({ ...gtmImportContext, ...updatedContext.requestBody });
    if (updatedContext.requestBody.SessionId !== sessionIdInContext) {
      wizardContext.dispatch({
        type: WIZARD_ACTIONS.WIZARD_UPDATE_GTM_SESSION_CONTEXT,
        GTMContext: {
          ...wizardContext.GTMContext,
          loginEmail: updatedContext.loginEmail,
          sessionId: updatedContext.requestBody.SessionId,
          GTMAccountId: null,
          GTMContainerId: null,
          GTMAccountName: null,
          GTMContainerName: null,
        },
      });
    } else {
      const updateGTMCloningCheckedOption = typeof updatedContext.cloneExistingTags === 'boolean' &&
      updatedContext.cloneExistingTags !== taskItemBasics.ImportUserPreference.ImportNewEntities;

      wizardContext.dispatch({
        type: WIZARD_ACTIONS.WIZARD_UPDATE_GTM_SESSION_CONTEXT,
        GTMContext: {
          ...wizardContext.GTMContext,
          ...updatedContext,
          ...(updatedContext.requestBody ? updatedContext.requestBody : {}),
          ...(updateGTMCloningCheckedOption ? { IsGTMCloningChecked: updatedContext.cloneExistingTags } : {}),
        },
      });

      if (updateGTMCloningCheckedOption) {
        setTaskItemBasics({
          ...taskItemBasics,
          ImportUserPreference: {
            ...taskItemBasics.ImportUserPreference,
            ImportNewEntities: updatedContext.cloneExistingTags,
          },
        });
      }
    }
  }, [gtmImportContext, sessionIdInContext, taskItemBasics, wizardContext]);

  const handleCancel = useCallback(() => {
    logActivity.trace('[GoalCreationWizard][GtmBaseTagSetup] cancelStep');
    const logObject = {
      currentStep: `thirdParty.${selectedPlatform}`,
    };
    logActivity.trace(`[GoalCreationWizard][GtmBaseTagSetup] complete log: ${logObject}`); // log drop off platform
    wizardContext.exitWizard();
  }, [logActivity, selectedPlatform, wizardContext]);

  const isCustomerLevel = !props.scope.values.aid;

  const submitTaskItem = useCallback(() => {
    setIsPollTaskStarted(true);
    // create task item
    const taskItem = {
      ...taskItemBasics,
      GTMImportContext: gtmImportContext,
    };
    repository.createGTMImportTaskItem(taskItem, isCustomerLevel)
      .then((res) => {
        if (_.isArray(res.TaskItemExecutions)) {
          // get task execution status
          const taskItemExecutionId = _.first(res.TaskItemExecutions).Id;
          repository.pollTaskExecutionStatus(taskItemExecutionId)
            .then((data) => {
              if (data.Status !== 'Completed' && data.Status !== 'InProgress' && data.Status !== 'PendingExecution') {
                // handle other status
                notificationApi.showWarning(i18n.getString(_TL_('Unexpected error happened.')));
              }
              logActivity.trace(`[GoalCreationWizard][GtmBaseTagSetup]: task submitted. status: ${data.Status}`);
              setIsPollTaskStarted(false);
              wizardContext.dispatch({
                type: WIZARD_ACTIONS.WIZARD_NAVIGATE_NEXT,
                data: {
                  GTMContext: {
                    ...wizardContext.GTMContext,
                    workflowCompleted: true,
                    publishedVersionId: data.PublishedVersionId,
                  },
                },
              });
            })
            .catch((err) => {
              const { errorCode } = err.customized;
              setIsPollTaskStarted(false); // stop blocking and stay at the current page
              notificationApi.showWarning(i18n.getString(gtmErrorCodeMapping[errorCode] || gtmErrorCodeMapping[65253]));
            });
        }
      });
  }, [taskItemBasics, gtmImportContext, repository, logActivity, wizardContext, i18n, isCustomerLevel]);

  const commonFooterButtons = useMemo(() => ({
    BACK: {
      type: 'Secondary',
      onClick: () => wizardContext.goBack(),
      isEnabled: true,
      text: i18n.getString(_TL_('Back')),
    },
    DONE: {
      type: 'Primary',
      onClick: submitTaskItem,
      isEnabled: !!gtmImportContext.GTMContainerId,
      text: i18n.getString(_TL_('Next')),
    },
    CANCEL: {
      type: 'Cancel',
      onClick: handleCancel,
      isEnabled: true,
      text: i18n.getString(_TL_('Cancel')),
    },
  }), [gtmImportContext.GTMContainerId, handleCancel, i18n, submitTaskItem, wizardContext]);

  const footerConfig = useMemo(() => {
    const config = {};
    config.buttons = [commonFooterButtons.BACK, commonFooterButtons.DONE, commonFooterButtons.CANCEL];
    return config;
  }, [commonFooterButtons]);

  const pageTitle = useMemo(() => i18n.getString(titleArray[footerConfigId]), [footerConfigId, i18n]);

  const handleGoogleLoggedOut = useCallback(() => setFooterConfigId(1), []);
  const handleGoogleSelectAccount = useCallback(() => setFooterConfigId(2), []);

  return (
    <UiBlocker
      blocking={isPollTaskStarted}
      loadingText={i18n.getString(_TL_('Loading...'))}
    >
      <StepContainer
        title={pageTitle}
        step="third-party-tag-step"
        footerConfig={footerConfig}
      >
        <ThirdPartyTagView
          tagContext={tagContext}
          selectedPlatform={selectedPlatform}
          isSmartOrExpertMode
          onCopyCallback={_.noop}
          permissions={wizardContext.permissions}
          serverContext={serverContext}
          logTrace={logActivity.trace}
          onGoogleLoggedOut={handleGoogleLoggedOut}
          onGoogleSelectAccount={handleGoogleSelectAccount}
          updateGTMImportContext={updateGTMImportContext}
          deps={{
            token,
            repository,
            isAllDependenciesLoaded,
          }}
          gtmUIContext={{
            ...wizardContext.GTMContext,
            AllowTagCloning: true,
          }}
        />
      </StepContainer>
    </UiBlocker>
  );
});

GtmBaseTagSetupStep.propTypes = {
  selectedPlatform: PropTypes.string,
  serverContext: PropTypes.objectOf(PropTypes.string),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.string,
    }),
  }),
};

GtmBaseTagSetupStep.defaultProps = {
  selectedPlatform: null,
  serverContext: {},
};
